/**
 * Mac Studio Menu Bar
 *
 * macOS Sonoma-style menu bar with frosted glass effect.
 * Provides access to theme controls, navigation, and user actions.
 */

import { useState, useRef, useEffect, useMemo, ReactNode } from 'react'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { useDesignToken } from '@/contexts/DesignTokenContext'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import { useAuth } from '@/contexts/AuthContext'
import { useWindowManagerSafe } from '@/contexts/WindowManagerContext'
import { LAYOUT_THEMES, LAYOUT_THEME_CONFIGS } from '@/types/layout-theme'
import { DESIGN_TOKEN_SETS, DESIGN_TOKEN_SET_CONFIGS } from '@/types/design-token-set'
import { VIEWER_PROFILES, VIEWER_PROFILE_CONFIGS } from '@/types/viewer-profile'
import { useWallpaper } from '@/hooks/useWallpaper'

// Menu item types
interface MenuItemBase {
  id: string
  label: string
  shortcut?: string
  disabled?: boolean
  hidden?: boolean
}

interface MenuItemAction extends MenuItemBase {
  type?: 'action'
  action: () => void
}

interface MenuItemSubmenu extends MenuItemBase {
  type: 'submenu'
  items: MenuItem[]
  current?: string
}

interface MenuItemDivider {
  type: 'divider'
  hidden?: boolean
}

type MenuItem = MenuItemAction | MenuItemSubmenu | MenuItemDivider

interface Menu {
  id: string
  label: string | ReactNode
  items: MenuItem[]
}

export interface MacStudioMenuBarProps {
  className?: string
}

export function MacStudioMenuBar({ className = '' }: MacStudioMenuBarProps) {
  const { themeConfig, setTheme } = useLayoutTheme()
  const { currentTokenSet, setTokenSet } = useDesignToken()
  const { currentProfile, setProfile } = useViewerProfile()
  const { isAuthenticated, user } = useAuth()
  const windowManager = useWindowManagerSafe()
  const wallpaperContext = useWallpaper()

  // Extract wallpaper values
  const currentWallpaper = wallpaperContext.currentWallpaper
  const allWallpapers = wallpaperContext.allWallpapers
  const setWallpaper = wallpaperContext.setWallpaper
  const rotationEnabled = wallpaperContext.rotationEnabled
  const setRotationEnabled = wallpaperContext.setRotationEnabled

  // Get current windows from context (reactive)
  const windows = windowManager?.windows ?? {}

  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  const menuBarRef = useRef<HTMLDivElement>(null)

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuBarRef.current && !menuBarRef.current.contains(event.target as Node)) {
        setActiveMenu(null)
        setActiveSubmenu(null)
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setActiveMenu(null)
        setActiveSubmenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  // Build menus dynamically - wrapped in useMemo for proper reactivity
  const menus: Menu[] = useMemo(() => [
    // Apple menu
    {
      id: 'apple',
      label: <span style={{ fontSize: '16px' }}></span>,
      items: [
        {
          id: 'about',
          label: 'About This Portfolio',
          action: () => {
            // Could open an about modal
            console.log('About')
            setActiveMenu(null)
          },
        },
        { type: 'divider' },
        ...(isAuthenticated ? [
          {
            id: 'dashboard',
            label: 'Dashboard',
            shortcut: '⌘D',
            action: () => {
              window.location.href = '/admin'
              setActiveMenu(null)
            },
          },
          { type: 'divider' as const },
          {
            id: 'sign-out',
            label: `Sign Out (${user?.displayName || user?.email || 'User'})`,
            action: () => {
              window.location.href = '/handler/sign-out'
            },
          },
        ] : [
          {
            id: 'sign-in',
            label: 'Sign In with GitHub...',
            action: () => {
              window.location.href = '/handler/sign-in'
            },
          },
        ]) as MenuItem[],
      ],
    },
    // App name menu
    {
      id: 'app',
      label: <strong>Agustin Musi</strong>,
      items: [
        {
          id: 'about-app',
          label: 'About Agustin Musi',
          action: () => {
            // Focus the hero window instead of navigating
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('hero-window')
              windowManager.focusWindow('hero-window')
            }
            setActiveMenu(null)
          },
        },
        { type: 'divider' },
        {
          id: 'hide',
          label: 'Hide Portfolio',
          shortcut: '⌘H',
          disabled: true,
          action: () => {},
        },
      ],
    },
    // View menu
    {
      id: 'view',
      label: 'View',
      items: [
        {
          id: 'wallpaper',
          type: 'submenu',
          label: 'Wallpaper',
          current: currentWallpaper.id,
          items: [
            ...allWallpapers.map(wp => ({
              id: wp.id,
              label: wp.name,
              action: () => {
                setWallpaper(wp.id)
                setActiveMenu(null)
                setActiveSubmenu(null)
              },
            })),
            { type: 'divider' as const },
            {
              id: 'rotation',
              label: rotationEnabled ? 'Disable Auto-Rotation' : 'Enable Auto-Rotation',
              action: () => {
                setRotationEnabled(!rotationEnabled)
                setActiveMenu(null)
                setActiveSubmenu(null)
              },
            },
          ],
        },
        { type: 'divider' },
        {
          id: 'layout',
          type: 'submenu',
          label: 'Layout Theme',
          current: themeConfig.id,
          items: LAYOUT_THEMES.map(theme => ({
            id: theme,
            label: LAYOUT_THEME_CONFIGS[theme].name,
            action: () => {
              setTheme(theme)
              setActiveMenu(null)
              setActiveSubmenu(null)
            },
          })),
        },
        {
          id: 'colors',
          type: 'submenu',
          label: 'Color Tokens',
          current: currentTokenSet,
          items: DESIGN_TOKEN_SETS.map(token => ({
            id: token,
            label: DESIGN_TOKEN_SET_CONFIGS[token].name,
            action: () => {
              setTokenSet(token)
              setActiveMenu(null)
              setActiveSubmenu(null)
            },
          })),
        },
        {
          id: 'perspective',
          type: 'submenu',
          label: 'Viewer Perspective',
          current: currentProfile,
          items: VIEWER_PROFILES.map(profile => ({
            id: profile,
            label: VIEWER_PROFILE_CONFIGS[profile].name,
            action: () => {
              // Use View Transitions API for smooth profile switching
              // This prevents jitter from DOM reconstruction
              const updateProfile = () => {
                setProfile(profile)
                setActiveMenu(null)
                setActiveSubmenu(null)
              }

              if (document.startViewTransition) {
                document.startViewTransition(updateProfile)
              } else {
                updateProfile()
              }
            },
          })),
        },
      ],
    },
    // Go menu - opens windows like macOS apps
    {
      id: 'go',
      label: 'Go',
      items: [
        {
          id: 'portfolio',
          label: 'Portfolio Finder',
          shortcut: '⌘1',
          action: () => {
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('portfolio-window')
              windowManager.focusWindow('portfolio-window')
            }
            setActiveMenu(null)
          },
        },
        {
          id: 'hero',
          label: 'Agustin Musi',
          shortcut: '⌘2',
          action: () => {
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('hero-window')
              windowManager.focusWindow('hero-window')
            }
            setActiveMenu(null)
          },
        },
        {
          id: 'tech-stack',
          label: 'Tech Stack',
          shortcut: '⌘3',
          action: () => {
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('tech-window')
              windowManager.focusWindow('tech-window')
            }
            setActiveMenu(null)
          },
        },
        {
          id: 'stats',
          label: 'Career Stats',
          shortcut: '⌘4',
          action: () => {
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('stats-window')
              windowManager.focusWindow('stats-window')
            }
            setActiveMenu(null)
          },
        },
        { type: 'divider' },
        {
          id: 'gated-content',
          label: 'Gated Content',
          action: () => {
            if (windowManager?.isEnabled) {
              windowManager.restoreWindow('gated-content-window')
              windowManager.focusWindow('gated-content-window')
            }
            setActiveMenu(null)
          },
        },
      ],
    },
    // Window menu
    {
      id: 'window',
      label: 'Window',
      items: [
        {
          id: 'minimize',
          label: 'Minimize',
          shortcut: '⌘M',
          disabled: true,
          action: () => {},
        },
        {
          id: 'zoom',
          label: 'Zoom',
          disabled: true,
          action: () => {},
        },
        { type: 'divider' },
        // Window arrangement options
        {
          id: 'smart-fit',
          label: 'Smart Fit',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.smartFit()
            setActiveMenu(null)
          },
        },
        {
          id: 'cascade',
          label: 'Cascade',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.cascadeWindows()
            setActiveMenu(null)
          },
        },
        {
          id: 'tile-all',
          label: 'Tile All',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.tileAllWindows()
            setActiveMenu(null)
          },
        },
        {
          id: 'center-all',
          label: 'Center on Screen',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.centerAllWindows()
            setActiveMenu(null)
          },
        },
        {
          id: 'restore-defaults',
          label: 'Restore Defaults',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.restoreDefaults()
            setActiveMenu(null)
          },
        },
        { type: 'divider' },
        {
          id: 'show-desktop',
          label: 'Show Desktop',
          shortcut: '⌘D',
          disabled: !windowManager?.isEnabled,
          action: () => {
            windowManager?.showDesktop()
            setActiveMenu(null)
          },
        },
        {
          id: 'bring-to-front',
          label: 'Bring All to Front',
          disabled: !windowManager?.isEnabled,
          action: () => {
            // Restore all minimized windows
            Object.keys(windows).forEach(id => {
              const state = windows[id]
              if (state?.isMinimized) {
                windowManager?.restoreWindow(id)
              }
            })
            setActiveMenu(null)
          },
        },
        { type: 'divider' },
        // Dynamic window list - uses reactive windows variable
        ...Object.entries(windows).map(([id, state]) => {
          const windowNames: Record<string, string> = {
            'portfolio-window': 'Portfolio Finder',
            'hero-window': 'Agustin Musi',
            'gated-content-window': 'Gated Content',
            'stats-window': 'Career Stats',
            'tech-window': 'Tech Stack',
          }
          return {
            id: `window-${id}`,
            label: `${state.isMinimized ? '' : '✓ '}${windowNames[id] || id}`,
            action: () => {
              if (state.isMinimized) {
                windowManager?.restoreWindow(id)
                windowManager?.focusWindow(id)
              } else {
                windowManager?.minimizeWindow(id)
              }
              setActiveMenu(null)
            },
          }
        }),
      ],
    },
  ], [
    isAuthenticated, user, themeConfig, currentTokenSet, currentProfile,
    currentWallpaper, allWallpapers, rotationEnabled, windows,
    setTheme, setTokenSet, setProfile, setWallpaper, setRotationEnabled, windowManager
  ])

  const handleMenuClick = (menuId: string) => {
    if (activeMenu === menuId) {
      setActiveMenu(null)
      setActiveSubmenu(null)
    } else {
      setActiveMenu(menuId)
      setActiveSubmenu(null)
    }
  }

  const handleMenuHover = (menuId: string) => {
    // Only switch menus on hover if a menu is already open
    if (activeMenu !== null) {
      setActiveMenu(menuId)
      setActiveSubmenu(null)
    }
  }

  const handleItemClick = (item: MenuItem) => {
    if (item.type === 'divider') return
    if ('disabled' in item && item.disabled) return

    if (item.type === 'submenu') {
      setActiveSubmenu(activeSubmenu === item.id ? null : item.id)
    } else if ('action' in item) {
      item.action()
    }
  }

  // Get current time for menu bar
  const [time, setTime] = useState(new Date())
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <div ref={menuBarRef} className={`mac-menubar ${className}`}>
      {/* Left side - Menus */}
      <div className="mac-menubar__left">
        {menus.map(menu => (
          <div key={menu.id} className="mac-menubar__menu">
            <button
              className={`mac-menubar__trigger ${activeMenu === menu.id ? 'mac-menubar__trigger--active' : ''}`}
              onClick={() => handleMenuClick(menu.id)}
              onMouseEnter={() => handleMenuHover(menu.id)}
            >
              {menu.label}
            </button>

            {activeMenu === menu.id && (
              <div className="mac-menubar__dropdown">
                {menu.items
                  .filter(item => !('hidden' in item && item.hidden))
                  .map((item, index) => {
                    if (item.type === 'divider') {
                      return <div key={index} className="mac-menubar__divider" />
                    }

                    const isSubmenu = item.type === 'submenu'
                    const isActive = isSubmenu && activeSubmenu === item.id
                    const isCurrentSelection = isSubmenu && 'current' in item

                    return (
                      <div key={item.id} className="mac-menubar__item-wrapper">
                        <button
                          className={`mac-menubar__item ${('disabled' in item && item.disabled) ? 'mac-menubar__item--disabled' : ''}`}
                          onClick={() => handleItemClick(item)}
                          onMouseEnter={() => isSubmenu && setActiveSubmenu(item.id)}
                          disabled={'disabled' in item && item.disabled}
                        >
                          <span className="mac-menubar__item-label">
                            {isCurrentSelection && item.type === 'submenu' && (
                              <span className="mac-menubar__check">
                                {item.current === themeConfig.id || item.current === currentTokenSet || item.current === currentProfile ? '' : ''}
                              </span>
                            )}
                            {item.label}
                          </span>
                          {'shortcut' in item && item.shortcut && (
                            <span className="mac-menubar__shortcut">{item.shortcut}</span>
                          )}
                          {isSubmenu && (
                            <span className="mac-menubar__arrow">▶</span>
                          )}
                        </button>

                        {/* Submenu */}
                        {isSubmenu && isActive && 'items' in item && (
                          <div className="mac-menubar__submenu">
                            {item.items.map(subItem => {
                              if (subItem.type === 'divider') {
                                return <div key={Math.random()} className="mac-menubar__divider" />
                              }
                              const isCurrent = 'current' in item && item.current === subItem.id
                              return (
                                <button
                                  key={subItem.id}
                                  className="mac-menubar__item"
                                  onClick={() => 'action' in subItem && subItem.action()}
                                >
                                  <span className="mac-menubar__item-label">
                                    <span className="mac-menubar__check">{isCurrent ? '✓' : ''}</span>
                                    {subItem.label}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Right side - System tray */}
      <div className="mac-menubar__right">
        {isAuthenticated && user && (
          <span className="mac-menubar__user">
            {user.displayName || user.email?.split('@')[0] || 'User'}
          </span>
        )}
        <span className="mac-menubar__time">{formatTime(time)}</span>
      </div>
    </div>
  )
}
